import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { supabase } from '../lib/supabase';

/**
 * Hook for managing user preferences stored in Supabase
 * Provides CRUD operations and real-time sync across devices
 */
export const useUserPreferences = () => {
  const { publicKey } = useWallet();
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // Default preferences structure
  const defaultPreferences = {
    // Notification delivery
    email_enabled: true,
    push_enabled: true,
    sms_enabled: false,
    notification_email: '',
    
    // Notification types
    notify_offers: true,
    notify_offer_accepted: true,
    notify_offer_declined: true,
    notify_offer_expired: true,
    notify_sales: true,
    notify_purchases: true,
    notify_listings: true,
    notify_trades: true,
    notify_trade_accepted: true,
    notify_trade_declined: true,
    notify_price_alerts: true,
    notify_floor_price: true,
    notify_likes: true,
    notify_favorites: true,
    notify_follows: false,
    notify_capsule_drops: true,
    notify_collection_featured: true,
    notify_verification: true,
    notify_system: true,
    notify_marketing: false,
    
    // Privacy
    profile_visibility: 'public',
    show_wallet_address: true,
    show_activity: true,
    show_owned_pieces: true,
    show_created_pieces: true,
    show_trades: true,
    show_favorites: true,
    allow_offers: true,
    allow_trades: true,
    allow_messages: true,
    
    // Display
    language: 'en',
    currency_display: 'both',
    theme: 'dark',
    default_view: 'grid',
    pieces_per_page: 20,
    show_usd_prices: true,
    compact_mode: false,
    
    // Activity
    auto_favorite_purchases: false,
    auto_favorite_creations: false,
    track_views: true,
    track_portfolio_value: true,
    enable_recommendations: true,
    enable_trending: true,
    
    // Advanced
    priority_fee_level: 'medium',
    custom_priority_fee: null,
    slippage_tolerance: 1.0,
    auto_approve_enabled: false,
    auto_approve_threshold: null,
    
    // Metadata
    custom_settings: {}
  };

  /**
   * Fetch user preferences from Supabase
   */
  const fetchPreferences = useCallback(async () => {
    if (!publicKey) {
      setPreferences(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const walletAddress = publicKey.toString();
      
      // Try to fetch existing preferences
      const { data, error: fetchError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('wallet', walletAddress)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!data) {
        // If not found, create with defaults
        console.log('Creating new user preferences for wallet:', walletAddress);
        const { data: newData, error: insertError } = await supabase
          .from('user_preferences')
          .insert([{ wallet: walletAddress, ...defaultPreferences }])
          .select()
          .maybeSingle();

        if (insertError) throw insertError;
        
        if (!newData) {
          throw new Error('Failed to create user preferences');
        }
        
        setPreferences(newData);
        setLastSyncedAt(new Date(newData.last_synced_at));
      } else {
        setPreferences(data);
        setLastSyncedAt(new Date(data.last_synced_at));
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
      setError(err.message);
      // Fallback to defaults if database fails
      setPreferences(defaultPreferences);
    } finally {
      setIsLoading(false);
    }
  }, [publicKey]);

  /**
   * Update user preferences in Supabase
   */
  const updatePreferences = useCallback(async (updates) => {
    if (!publicKey) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      const walletAddress = publicKey.toString();
      
      // Merge updates with existing preferences
      const updatedPrefs = {
        ...preferences,
        ...updates,
        wallet: walletAddress,
        updated_at: new Date().toISOString(),
        last_synced_at: new Date().toISOString()
      };

      const { data, error: updateError } = await supabase
        .from('user_preferences')
        .update(updatedPrefs)
        .eq('wallet', walletAddress)
        .select()
        .maybeSingle();

      if (updateError) throw updateError;
      
      if (!data) {
        throw new Error('Failed to update user preferences - no data returned');
      }
      
      setPreferences(data);
      setLastSyncedAt(new Date(data.last_synced_at));
      
      return data;
    } catch (err) {
      console.error('Error updating user preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [publicKey, preferences]);

  /**
   * Update a single preference field
   */
  const updatePreference = useCallback(async (key, value) => {
    return updatePreferences({ [key]: value });
  }, [updatePreferences]);

  /**
   * Update multiple preference fields
   */
  const updateMultiplePreferences = useCallback(async (updates) => {
    return updatePreferences(updates);
  }, [updatePreferences]);

  /**
   * Reset preferences to defaults
   */
  const resetPreferences = useCallback(async () => {
    if (!publicKey) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      const walletAddress = publicKey.toString();
      
      // Delete existing preferences
      await supabase
        .from('user_preferences')
        .delete()
        .eq('wallet', walletAddress);
      
      // Create new with defaults
      const { data, error: insertError } = await supabase
        .from('user_preferences')
        .insert([{ wallet: walletAddress, ...defaultPreferences }])
        .select()
        .maybeSingle();

      if (insertError) throw insertError;
      
      if (!data) {
        throw new Error('Failed to reset user preferences - no data returned');
      }
      
      setPreferences(data);
      setLastSyncedAt(new Date(data.last_synced_at));
      
      return data;
    } catch (err) {
      console.error('Error resetting user preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [publicKey]);

  /**
   * Get a specific preference value
   */
  const getPreference = useCallback((key, defaultValue = null) => {
    if (!preferences) return defaultValue;
    return preferences[key] ?? defaultValue;
  }, [preferences]);

  /**
   * Check if notifications are enabled for a specific type
   */
  const isNotificationEnabled = useCallback((notificationType) => {
    if (!preferences) return true; // Default to enabled
    const key = `notify_${notificationType}`;
    return preferences[key] ?? true;
  }, [preferences]);

  // Load preferences when wallet connects
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Set up real-time subscription for preference updates
  useEffect(() => {
    if (!publicKey) return;

    const walletAddress = publicKey.toString();
    
    const subscription = supabase
      .channel(`user_preferences:${walletAddress}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_preferences',
          filter: `wallet=eq.${walletAddress}`
        },
        (payload) => {
          console.log('Preferences updated from another device:', payload);
          setPreferences(payload.new);
          setLastSyncedAt(new Date(payload.new.last_synced_at));
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [publicKey]);

  return {
    preferences,
    isLoading,
    isSaving,
    error,
    lastSyncedAt,
    updatePreference,
    updatePreferences: updateMultiplePreferences,
    resetPreferences,
    getPreference,
    isNotificationEnabled,
    refreshPreferences: fetchPreferences,
    defaultPreferences
  };
};

export default useUserPreferences;

